import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import DownloadIcon from "@mui/icons-material/Download";
import { numberWithCommas } from "../../utils";
import { PurchaseOrderImportReportRow } from "../../types/purchase-order.type";
import { downloadUnreadableRowsCsv } from "./reportCsv";
import { CARD_CONTENT_SX, CARD_SX } from "./constants";

/** ไฟล์จริงอาจมีแถวเสียหลายร้อยแถว แสดงบนจอพอให้เห็นรูปแบบ ที่เหลืออยู่ในไฟล์ CSV */
const MAX_ROWS_SHOWN = 50;

interface UnreadableRowsCardProps {
  rows: PurchaseOrderImportReportRow[];
  /** ใช้ตั้งชื่อไฟล์ CSV ให้ผูกกับผลตรวจครั้งนี้ */
  uploadId: string;
}

/**
 * แถวที่ parser อ่านไม่ได้ — ไม่ถูกบันทึกและไม่บล็อกการยืนยัน
 *
 * แถวว่างและหัวตารางที่ซ้ำกลางไฟล์ถูกข้ามเงียบ ๆ ตั้งแต่ฝั่ง parser แล้ว สิ่งที่ขึ้นตรงนี้
 * คือแถวที่มีข้อมูลแต่ระบบตีความไม่ได้จริง ๆ จึงควรให้คนเปิดไฟล์ไปดูตามตำแหน่ง
 */
const UnreadableRowsCard = ({ rows, uploadId }: UnreadableRowsCardProps) => {
  const visibleRows = rows.slice(0, MAX_ROWS_SHOWN);
  const hiddenCount = rows.length - visibleRows.length;

  const handleDownload = () => {
    downloadUnreadableRowsCsv(
      rows,
      `po-import-unreadable-${uploadId.slice(0, 8)}.csv`,
    );
  };

  return (
    <Card sx={CARD_SX}>
      <CardContent sx={CARD_CONTENT_SX}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
          mb={1}
        >
          <Typography variant="subtitle1">
            แถวที่อ่านไม่ได้ ({numberWithCommas(rows.length)})
          </Typography>
          <Button
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
          >
            ดาวน์โหลด CSV
          </Button>
        </Stack>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ตำแหน่งในไฟล์</TableCell>
              <TableCell>แถว</TableCell>
              <TableCell>ข้อความในเซลล์</TableCell>
              <TableCell>สาเหตุ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, index) => (
              <TableRow key={`${row.location}-${index}`}>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {row.location}
                </TableCell>
                <TableCell>{row.row_no}</TableCell>
                <TableCell>{row.cell_text}</TableCell>
                <TableCell>{row.reason}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {hiddenCount > 0 && (
          <Typography variant="body2" color="text.secondary" mt={1}>
            ยังมีอีก {numberWithCommas(hiddenCount)} แถว ดาวน์โหลด CSV
            เพื่อดูทั้งหมด
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default UnreadableRowsCard;
